<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { useLongPressTooltip } from "@/composable/useLongPressTooltip";
import { SaveState, manualSave } from "@/stores/SaveDrawingState";
import Settings from "@/stores/Settings";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const { t } = useTranslation();
const { refHook } = useLongPressTooltip();

function handleClick() {
  if (SaveState.value === "changed") {
    manualSave();
  }
}

// Promettre une sauvegarde automatique alors que le réglage est sur Non serait
// le pire des mensonges pour ce badge.
const tooltipContent = computed(() => {
  const hasChanges = SaveState.value === "changed";
  if (!Settings.autoSaveOn) {
    return hasChanges
      ? t("canvas:save.tooltip.clickToSaveNoAutoSave")
      : t("canvas:save.tooltip.textNoAutoSave");
  }
  return hasChanges
    ? t("canvas:save.tooltip.clickToSave")
    : t("canvas:save.tooltip.text");
});
</script>

<template>
  <span
    ref="refHook"
    class="save-badge position-relative"
    :class="{ clickable: SaveState === 'changed' }"
    :data-bs-content="tooltipContent"
    :data-bs-title="t('canvas:save.tooltip.title')"
    @click="handleClick"
  >
    <PhosphorIcon name="floppy-disk" :size="20" opaci />
    <!-- Coin bas droit de la disquette : la coche verte (enregistré) et la
         pastille orange (modifications non enregistrées) au même endroit, à
         la même taille. L'enregistrement en cours est montré par SaveOverlay. -->
    <div class="badge-position">
      <span
        v-if="SaveState === 'saved'"
        class="save-indicator save-indicator-saved"
      >
        <PhosphorIcon name="check" :size="8" weight="bold" />
      </span>
      <span
        v-else-if="SaveState === 'changed'"
        class="save-indicator save-indicator-changed"
      ></span>
    </div>
  </span>
</template>

<style lang="scss">
.save-badge {
  color: var(--bs-secondary);

  &.clickable {
    cursor: pointer;

    &:hover {
      color: var(--bs-primary);
    }
  }
}

.badge-position {
  position: absolute;
  bottom: -6px;
  right: -3px;
  display: flex;
}

.save-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 12px;
  height: 12px;
  border: 1px solid var(--bs-body-bg);
  border-radius: 50%;
}

.save-indicator-saved {
  background-color: var(--bs-success);
  color: #fff;
}

.save-indicator-changed {
  background-color: var(--bs-warning);
}

.circle {
  border-radius: 50%;
}
</style>
